import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import DemoModeBanner from '../../components/demo/DemoModeBanner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PortfolioSummary from './components/PortfolioSummary';
import AssetAllocationChart from './components/AssetAllocationChart';
import AssetTable from './components/AssetTable';
import AddAssetModal from './components/AddAssetModal';
import PerformanceTracker from './components/PerformanceTracker';
import SpecialEventsSection from './components/SpecialEventsSection';
import { useAssets } from '../../hooks/useAssets';
import { buildApiUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useDemo } from '../../contexts/DemoContext';
import { useTour } from '../../contexts/TourContext';
import VideoModal from '../../components/ui/VideoModal';
import { getVideoForPage } from '../../config/videoConfig';

const AssetPortfolio = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  
  // Use the assets hook for API integration
  const { 
    assets, 
    loading, 
    error, 
    createAsset, 
    updateAsset, 
    deleteAsset 
  } = useAssets();
  const { getAuthHeaders } = useAuth();
  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  const { startAssetPortfolioTour, autoStartTour } = useTour();
  const videoConfig = getVideoForPage('asset-portfolio');

  const handleAddAsset = async (assetData) => {
    try {
      await createAsset(assetData);
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error adding asset:', error);
    }
  };

  const handleEditAsset = (asset) => {
    setEditingAsset(asset);
    setIsAddModalOpen(true);
  };

  const handleSaveAsset = async (assetData) => {
    try {
      if (editingAsset) {
        // Update existing asset
        await updateAsset(assetData.id, assetData);
        setEditingAsset(null);
      } else {
        // Add new asset
        await createAsset(assetData);
      }
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error saving asset:', error);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      await deleteAsset(assetId);
    } catch (error) {
      console.error('Error deleting asset:', error);
    }
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingAsset(null);
  };

  // Auto-start tour for first-time visitors
  useEffect(() => {
    if (!loading && assets) {
      autoStartTour('asset-portfolio', startAssetPortfolioTour);
    }
  }, [loading, assets, autoStartTour, startAssetPortfolioTour]);

  // Filter assets based on active tab
  const filteredAssets = activeTab === 'All'
    ? assets
    : activeTab === 'FD & Cash'
      ? assets.filter(asset => asset.type === 'Fixed Deposits' || asset.type === 'Cash')
      : assets.filter(asset => asset.type === activeTab);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading portfolio data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Data</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const assetCategories = ['All', 'Equity', 'Mutual Funds', 'Real Estate', 'PPF', 'FD & Cash', 'Gold'];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isExpanded={sidebarExpanded} 
        onToggle={() => setSidebarExpanded(!sidebarExpanded)} 
      />
      
      <div className={`
        transition-all duration-300 ease-spring
        ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
      `}>
        <Header 
          sidebarExpanded={sidebarExpanded}
          onSidebarToggle={() => setSidebarExpanded(!sidebarExpanded)}
        />
        
        <DemoModeBanner />
        
        <main className="pt-16 pb-20 lg:pb-8">
          <div className="p-4 sm:p-6 py-6 sm:py-8">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8" data-tour="asset-header">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Asset Portfolio</h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  Track and manage your investment portfolio with comprehensive analysis and projections
                </p>
              </div>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  iconName="Video"
                  iconPosition="left"
                  onClick={() => setIsVideoOpen(true)}
                  className="hidden sm:inline-flex text-xs sm:text-sm"
                >
                  Learn
                </Button>
                <Button
                  variant="outline"
                  iconName="HelpCircle"
                  iconPosition="left"
                  onClick={startAssetPortfolioTour}
                  className="hidden sm:inline-flex text-xs sm:text-sm"
                >
                  Tour
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setIsAddModalOpen(true)}
                  className="text-xs sm:text-sm disabled:opacity-60"
                  disabled={!effectiveCanWrite}
                >
                  <span className="hidden sm:inline">Add Asset</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </div>
            </div>

            {/* Portfolio Summary */}
            <div data-tour="asset-summary">
              <PortfolioSummary assets={assets} />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {/* Left Column - Asset Management */}
              <div className="xl:col-span-3 space-y-8">
                {/* Asset Categories Tabs */}
                <div className="bg-card rounded-lg border border-border p-6" data-tour="asset-table">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="Package" size={16} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Asset Categories</h2>
                        <p className="text-sm text-muted-foreground">
                          {filteredAssets.length} assets in {activeTab === 'All' ? 'all categories' : activeTab.toLowerCase()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Category Tabs */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {assetCategories.map(category => {
                      const categoryAssets = category === 'All'
                        ? assets
                        : category === 'FD & Cash'
                          ? assets.filter(a => a.type === 'Fixed Deposits' || a.type === 'Cash')
                          : assets.filter(a => a.type === category);
                      const isActive = activeTab === category;
                      
                      return (
                        <button
                          key={category}
                          onClick={() => setActiveTab(category)}
                          className={`
                            flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                            ${isActive 
                              ? 'bg-primary text-primary-foreground shadow-soft' 
                              : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                            }
                          `}
                        >
                          <Icon 
                            name={
                              category === 'All' ? 'Grid3X3' :
                              category === 'Equity' ? 'TrendingUp' :
                              category === 'Mutual Funds' ? 'PieChart' :
                              category === 'Real Estate' ? 'Home' :
                              category === 'PPF' ? 'Shield' :
                              category === 'FD & Cash' ? 'Banknote' : 'Coins'
                            } 
                            size={16} 
                          />
                          {category}
                          <span className={`
                            px-2 py-0.5 rounded-full text-xs
                            ${isActive 
                              ? 'bg-primary-foreground/20 text-primary-foreground' 
                              : 'bg-background text-muted-foreground'
                            }
                          `}>
                            {categoryAssets.length}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Asset Table */}
                  <AssetTable
                    assets={filteredAssets}
                    selectedCategory={activeTab}
                    onEditAsset={handleEditAsset}
                    onDeleteAsset={handleDeleteAsset}
                  />
                </div>

                {/* Special Events Section inside Portfolio */}
                <div data-tour="asset-events">
                  <SpecialEventsSection />
                </div>

                {/* Performance Tracking */}
                <div className="bg-card rounded-lg border border-border p-6" data-tour="asset-performance">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Icon name="Activity" size={16} className="text-accent" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Performance Analysis</h2>
                      <p className="text-sm text-muted-foreground">Projected across your setup horizon</p>
                    </div>
                  </div>
                  <PerformanceTracker assets={assets} />
                </div>

                {/* Asset Allocation Chart */}
                <div data-tour="asset-allocation">
                  <AssetAllocationChart assets={assets} showEvolution={true} />
                </div>

                {/* Risk Assessment */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                      <Icon name="AlertTriangle" size={16} className="text-warning" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Risk Assessment</h3>
                      <p className="text-sm text-muted-foreground">Portfolio risk analysis</p>
                    </div>
                  </div>

                  <RiskAssessmentPanel />
                </div>
              </div>
              
            </div>
          </div>
        </main>
      </div>

      {/* Add/Edit Asset Modal */}
      <AddAssetModal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        onAddAsset={handleSaveAsset}
        editingAsset={editingAsset}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl={videoConfig?.url}
        title={videoConfig?.title}
      />
    </div>
  );
};

export default AssetPortfolio;

const RiskAssessmentPanel = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    const fetchRisk = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/asset/portfolio/risk'), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load risk');
        const json = await res.json();
        if (json?.success) setData(json.data);
      } catch (e) {
        console.error(e);
        setData(null);
      }
    };
    fetchRisk();
  }, []);
  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="Shield" size={16} className="text-accent" />
            <span className="text-sm font-medium">Overall Risk</span>
          </div>
          <span className="text-sm font-medium text-warning">{data?.overallRisk || '—'}</span>
        </div>

        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="Target" size={16} className="text-primary" />
            <span className="text-sm font-medium">Diversification</span>
          </div>
          <span className="text-sm font-medium text-accent">{data?.diversification || '—'}</span>
        </div>

        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="Droplets" size={16} className="text-primary" />
            <span className="text-sm font-medium">Liquidity</span>
          </div>
          <span className="text-sm font-medium text-accent">{data?.liquidity || '—'}</span>
        </div>
      </div>

      <div className="mt-6 p-4 bg-accent/5 border border-accent/20 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={16} className="text-accent mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Recommendation</h4>
            <p className="text-xs text-muted-foreground">
              {data?.recommendation || '—'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};